import { create } from "zustand";

/** Brand review (coverage) → industry market step → finish. */
export type PersonaOnboardingStep = "brand" | "industry";

interface PersonaOnboardingState {
  /** Index of the focused suggestion in the review carousel. */
  focusIndex: number;
  step: PersonaOnboardingStep;
  /**
   * Client-minted UUID for the current review batch — tags every reject until
   * Finish, so DS can retag the whole batch. Persisted per tenant in
   * sessionStorage so refresh mid-review keeps the same id.
   */
  sessionId: string | null;
  /** Tenant `sessionId` belongs to — guards against reuse after a tenant switch. */
  sessionTenantId: string | null;
  setFocusIndex: (index: number) => void;
  setStep: (step: PersonaOnboardingStep) => void;
  /** Mints or restores a session id for `tenantId`; reused until clearSession(). */
  startSession: (tenantId: string) => string;
  /** Always mints a fresh batch id — use when opening a new review pass. */
  mintSession: (tenantId: string) => string;
  /** Drops carousel UI state only — keeps the session id for summary → complete. */
  resetUi: () => void;
  /** Clears UI state and the persisted session for `tenantId`. */
  clearSession: (tenantId: string) => void;
  reset: () => void;
}

const INITIAL_UI_STATE = {
  focusIndex: 0,
  step: "brand" as PersonaOnboardingStep,
};

function onboardingSessionStorageKey(tenantId: string): string {
  return `nexus_personas_onboarding_session_${tenantId}`;
}

function readPersistedSession(tenantId: string): string | null {
  try {
    return sessionStorage.getItem(onboardingSessionStorageKey(tenantId));
  } catch {
    return null;
  }
}

function writePersistedSession(tenantId: string, sessionId: string): void {
  try {
    sessionStorage.setItem(onboardingSessionStorageKey(tenantId), sessionId);
  } catch {
    /* private browsing */
  }
}

function removePersistedSession(tenantId: string): void {
  try {
    sessionStorage.removeItem(onboardingSessionStorageKey(tenantId));
  } catch {
    /* private browsing */
  }
}

export const usePersonaOnboardingStore = create<PersonaOnboardingState>((set, get) => ({
  ...INITIAL_UI_STATE,
  sessionId: null,
  sessionTenantId: null,
  setFocusIndex: (focusIndex) => set({ focusIndex }),
  setStep: (step) => set({ step }),
  startSession: (tenantId) => {
    const persisted = readPersistedSession(tenantId);
    const { sessionId: inMemory, sessionTenantId } = get();

    // sessionStorage is the cross-tab source of truth for the active batch id.
    if (persisted) {
      if (sessionTenantId !== tenantId || inMemory !== persisted) {
        set({ sessionId: persisted, sessionTenantId: tenantId });
      }
      return persisted;
    }

    if (inMemory && sessionTenantId === tenantId) return inMemory;

    const sessionId = crypto.randomUUID();
    writePersistedSession(tenantId, sessionId);
    set({ sessionId, sessionTenantId: tenantId });
    return sessionId;
  },
  mintSession: (tenantId) => {
    const sessionId = crypto.randomUUID();
    writePersistedSession(tenantId, sessionId);
    set({ sessionId, sessionTenantId: tenantId });
    return sessionId;
  },
  resetUi: () => set(INITIAL_UI_STATE),
  clearSession: (tenantId) => {
    removePersistedSession(tenantId);
    set({ ...INITIAL_UI_STATE, sessionId: null, sessionTenantId: null });
  },
  reset: () => set({ ...INITIAL_UI_STATE, sessionId: null, sessionTenantId: null }),
}));
